/**
 * PATH: packages/ui/src/kanban/components/card-detail/FileVersionRail.tsx
 * FILE_ID: UI-KANBAN-CMP-FILE-VERSION-RAIL-001
 * PURPOSE: Version history rail for ONE card attachment's QDrive file —
 *          v1..vN list with open + "revert to this version" actions,
 *          expanded under a file chip in the drawer's Files tab.
 * WHY:     ARC-2 / U11, operator 2026-07-02: "This should also be
 *          versioned where I can go to previous versions and distribute
 *          them instead of the markups." Each markup save in the in-card
 *          editor snapshots the prior blob to a FileVersion; this rail is
 *          how the operator sees, reopens and reverts those versions.
 * FLOW:
 *   ┌──────────────────┐   ┌─────────────────────────┐   ┌──────────────────┐
 *   │ qdrive_file_id   │ → │ adapter.listFileVersions │ → │ vN rows: open /  │
 *   │ (attachment)     │   │ (react-query cached)     │   │ restore actions  │
 *   └──────────────────┘   └─────────────────────────┘   └──────────────────┘
 * DEPENDENCIES:
 *   - KanbanEmailAdapter.listFileVersions / restoreFileVersion /
 *     getVersionDownloadUrl: the tenant seam (@quotech/ui cannot call the
 *     backend directly nor import email-hub — cycle).
 * RELATED:
 *   - CardFilesRail.tsx: mounts this under panel-variant chips.
 *   - organisation/services/qdrive_extended/versioning.py: contract source.
 */
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useInRouterContext, useNavigate } from 'react-router-dom'
import { History, Loader2, RotateCcw, Send, SquareArrowOutUpRight } from 'lucide-react'

import { cn } from '../../../lib/utils'
import { formatFileSize } from '../../../components/email/attachmentClassify'
import type { KanbanEmailAdapter, KanbanFileVersionEntry } from '../../api'

export const fileVersionsQueryKey = (fileId: string) =>
  ['kanban', 'file-versions', fileId] as const

export interface FileVersionRailProps {
  /** The attachment's QDrive FileRecord id (`att.qdrive_file_id`). */
  fileId: string
  emailAdapter: KanbanEmailAdapter
}

function versionDate(iso?: string | null): string {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
}

export function FileVersionRail({ fileId, emailAdapter }: FileVersionRailProps) {
  const qc = useQueryClient()
  const [busyId, setBusyId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  // ARC-2 MR5 — distribute needs CLIENT-SIDE navigation (the pending
  // compose attachment lives in an in-memory store; a full page load
  // drops it). Router-context guard, same pattern as
  // CardRailHistoryBridge: outside a Router (isolated Storybook/tests)
  // the distribute button simply hides.
  const inRouter = useInRouterContext()

  const historyQ = useQuery({
    queryKey: fileVersionsQueryKey(fileId),
    queryFn: () => emailAdapter.listFileVersions!(fileId),
    enabled: !!emailAdapter.listFileVersions,
    staleTime: 15_000,
  })

  if (!emailAdapter.listFileVersions) return null

  async function onOpen(entry: KanbanFileVersionEntry) {
    if (!emailAdapter.getVersionDownloadUrl) return
    setBusyId(entry.id)
    setError(null)
    try {
      const url = await emailAdapter.getVersionDownloadUrl(fileId, entry.id)
      // Presigned S3 URL (inline disposition) — no auth header needed, so a
      // plain new-tab open renders the version in the browser viewer.
      window.open(url, '_blank', 'noopener')
    } catch {
      setError(`Could not open v${entry.version_number}`)
    } finally {
      setBusyId(null)
    }
  }

  async function onRestore(entry: KanbanFileVersionEntry) {
    if (!emailAdapter.restoreFileVersion) return
    setBusyId(entry.id)
    setError(null)
    try {
      await emailAdapter.restoreFileVersion(fileId, entry.id)
      // Restore rewrites the live blob + snapshots the pre-restore one —
      // the whole history changed shape, refetch it.
      await qc.invalidateQueries({ queryKey: fileVersionsQueryKey(fileId) })
    } catch {
      setError(`Could not revert to v${entry.version_number}`)
    } finally {
      setBusyId(null)
    }
  }

  if (historyQ.isLoading) {
    return (
      <div className="flex items-center gap-2 px-2 py-1.5 text-[11px] text-neutral-400 dark:text-neutral-500">
        <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> Loading versions…
      </div>
    )
  }

  const versions = historyQ.data?.versions ?? []
  if (historyQ.isError || versions.length === 0) {
    return (
      <div className="px-2 py-1.5 text-[11px] text-neutral-400 dark:text-neutral-500">
        {historyQ.isError ? 'Could not load version history.' : 'No versions yet — save a markup to create v2.'}
      </div>
    )
  }

  return (
    <div
      role="list"
      aria-label="File versions"
      className="space-y-0.5 rounded-md border border-neutral-200 bg-neutral-50/60 p-1 dark:border-zinc-800 dark:bg-zinc-900/40"
    >
      {error && (
        <p className="px-1.5 pt-0.5 text-[10.5px] text-rose-600 dark:text-rose-400">{error}</p>
      )}
      {versions.map((v) => {
        const busy = busyId === v.id
        return (
          <div
            key={v.id}
            role="listitem"
            className="flex items-center gap-2 rounded px-1.5 py-1 text-[11px] hover:bg-white dark:hover:bg-zinc-800/60"
          >
            <History className="h-3 w-3 shrink-0 text-neutral-400" aria-hidden />
            <span
              className={cn(
                'font-semibold',
                v.is_current
                  ? 'text-blue-600 dark:text-blue-400'
                  : 'text-neutral-600 dark:text-neutral-300',
              )}
            >
              v{v.version_number}
            </span>
            <span className="min-w-0 flex-1 truncate text-neutral-400 dark:text-neutral-500">
              {[versionDate(v.created_at), v.created_by, v.comment]
                .filter(Boolean)
                .join(' · ') || (v.is_current ? 'Current' : '')}
              {v.size ? ` · ${formatFileSize(v.size)}` : ''}
            </span>
            {v.is_current && (
              <span className="rounded bg-blue-500/10 px-1.5 py-px text-[9.5px] font-semibold uppercase tracking-wide text-blue-600 dark:text-blue-400">
                Current
              </span>
            )}
            {busy ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" aria-hidden />
            ) : (
              <span className="flex items-center gap-1">
                {emailAdapter.getVersionDownloadUrl && (
                  <button
                    type="button"
                    onClick={() => void onOpen(v)}
                    title={`Open v${v.version_number}`}
                    className="rounded p-1 text-neutral-400 hover:bg-neutral-200 hover:text-neutral-700 dark:hover:bg-zinc-700 dark:hover:text-zinc-200"
                  >
                    <SquareArrowOutUpRight className="h-3 w-3" aria-hidden />
                  </button>
                )}
                {inRouter && emailAdapter.distributeFileVersion && (
                  <DistributeButton
                    fileId={fileId}
                    entry={v}
                    filename={historyQ.data?.filename}
                    distribute={emailAdapter.distributeFileVersion.bind(emailAdapter)}
                    onBusy={(b) => setBusyId(b ? v.id : null)}
                    onError={() => setError(`Could not distribute v${v.version_number}`)}
                  />
                )}
                {/* Restore only makes sense for non-current SNAPSHOT rows —
                    the backend rejects current/orphan entries. */}
                {emailAdapter.restoreFileVersion && v.kind === 'snapshot' && !v.is_current && (
                  <button
                    type="button"
                    onClick={() => void onRestore(v)}
                    title={`Revert to v${v.version_number}`}
                    className="rounded p-1 text-neutral-400 hover:bg-neutral-200 hover:text-neutral-700 dark:hover:bg-zinc-700 dark:hover:text-zinc-200"
                  >
                    <RotateCcw className="h-3 w-3" aria-hidden />
                  </button>
                )}
              </span>
            )}
          </div>
        )
      })}
    </div>
  )
}

/** ARC-2 MR5 — "distribute this version" (send it by email). The adapter
 *  stashes the version's bytes as a pending compose attachment and hands
 *  back the compose URL; we navigate CLIENT-SIDE so the in-memory pending
 *  store survives into the composer. Own component so `useNavigate` only
 *  runs when the parent confirmed a Router exists. */
function DistributeButton({
  fileId,
  entry,
  filename,
  distribute,
  onBusy,
  onError,
}: {
  fileId: string
  entry: KanbanFileVersionEntry
  filename?: string
  distribute: NonNullable<KanbanEmailAdapter['distributeFileVersion']>
  onBusy: (busy: boolean) => void
  onError: () => void
}) {
  const navigate = useNavigate()
  async function onDistribute() {
    onBusy(true)
    try {
      const composeUrl = await distribute(fileId, entry.id, {
        versionNumber: entry.version_number,
        filename,
      })
      navigate(composeUrl)
    } catch {
      onError()
    } finally {
      onBusy(false)
    }
  }
  return (
    <button
      type="button"
      onClick={() => void onDistribute()}
      title={`Distribute v${entry.version_number} by email`}
      className="rounded p-1 text-neutral-400 hover:bg-neutral-200 hover:text-neutral-700 dark:hover:bg-zinc-700 dark:hover:text-zinc-200"
    >
      <Send className="h-3 w-3" aria-hidden />
    </button>
  )
}
